'use client'

/**
 * ScorePanel — componente orchestratore del segnapunti (portato da
 * `/opt/ws-dinamolfetta/src/components/live/ScorePanel.tsx` e adattato al
 * portale multi-club: nessuna dipendenza da `TeamId`/`TEAMS`).
 *
 * Puro display, nessuna logica: solo props.
 *
 * Varianti:
 *  'card'      — compatto, per viewer mobile e desktop
 *  'broadcast' — TV graphics per OBS (sfondo scuro, numeri grandi)
 */
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { MatchState, type LiveSide, type ScorebugThemeConfig } from '@/lib/live/state'
import { isMatchPoint, isSetPoint } from '@/lib/live/score-points'
import { fipavLogoProxyUrl } from '@/lib/logo/proxy-url'
import { contrastRatio, readableOnDark, readableOnLight, relativeLuminance } from '@/lib/live/contrast'
import MatchStatus from './MatchStatus'
import TeamBlock from './TeamBlock'
import ScoreBlock from './ScoreBlock'
import SetHistory from './SetHistory'
import { displayPartials } from '@/lib/live/partials'
import { presentMatch, winningSide, type BoardPresentation } from '@/lib/live/match-presentation'
import type { SubstitutionSwap } from '@/lib/live/substitution'
import { MonsterBlockScene } from './MonsterBlockScene'
import { AceScene } from './AceScene'

/**
 * Valori dei token `--sb-*` del **tema chiaro**. Il tema scuro sono i valori di
 * ripiego già sparsi nel componente (fondo quasi nero, testo bianco): qui non si
 * riscrivono. Il tema si applica con l'attributo `data-tone`, così cambia a caldo
 * senza ricostruire classi, e resta **sotto** a `config.theme`/`scorebugTheme`.
 */
const LIGHT_TONE_VARS: Record<string, string> = {
  '--sb-main-bg': 'linear-gradient(180deg, #ffffff, #eef3fa)',
  '--sb-details-bg': '#eef2f8',
  '--sb-center-slot-bg': 'linear-gradient(180deg, rgba(22,33,51,0.05), rgba(22,33,51,0.02))',
  '--sb-chip-bg': 'rgba(22,33,51,0.05)',
  '--sb-sets-bg': 'rgba(22,33,51,0.06)',
  '--sb-logo-bg': 'rgba(255,255,255,0)',
  '--sb-border-color': 'rgba(22,33,51,0.12)',
  '--sb-chip-border': 'rgba(22,33,51,0.16)',
  '--sb-text': '#162133',
  '--sb-text-soft': 'rgba(22,33,51,0.72)',
  '--sb-text-muted': 'rgba(22,33,51,0.48)',
  '--sb-secondary-text': '#ffffff',
  '--sb-win': '#15803d',
  '--sb-loss': '#b91c1c',
  // I bordi di vittoria/sconfitta sono tarati sul fondo scuro: sul bianco vanno
  // più marcati, altrimenti la cornice non si vede.
  '--sb-win-border': 'rgba(21, 128, 61, 0.45)',
  '--sb-loss-border': 'rgba(185, 28, 28, 0.4)',
}

interface Props {
  state: MatchState
  accentColor?: string
  variant?: 'card' | 'broadcast'
  /** Tema del tabellone: `dark` (default, comportamento storico) o `light`. */
  tone?: 'dark' | 'light'
  className?: string
  /**
   * Sostituzione in corso: nello spazio della squadra che cambia, punteggio,
   * set, timeout e badge lasciano il posto ai due numeri (esce → entra) per tre
   * cicli. Il resto del tabellone resta dov'è.
   */
  substitution?: SubstitutionSwap | null
  /**
   * Nomi per numero dalle distinte (`club_rosters`). Se la voce manca, il piè
   * mostra solo il numero: mai un nome inventato.
   */
  playerNames?: { home: Record<string, string>; away: Record<string, string> } | null
  /** Muro appena murato: la metà di quella squadra mette in scena i mattoni. */
  block?: { team: LiveSide } | null
  /**
   * Ace appena fatto: la scena va sulla metà **avversaria** (è lì che la palla
   * batte), e il punteggio di chi ha battuto resta leggibile.
   */
  ace?: { team: LiveSide } | null
  /**
   * Il commento va in onda solo **dopo** il punto attribuito dallo scorer. Chi ha
   * gli eventi (`useLiveSubstitution`) lo dice; senza, vale `true` (anteprima,
   * pagina pubblica), dove non c'è un flusso di eventi da guardare.
   */
  commentaryGate?: boolean
  /**
   * Coordinate della gara — data, ora e palazzetto — per le partite ancora
   * «Programmata»: a gara non iniziata i parziali non esistono, quindi il
   * mini-tabellone mostra **dove e quando si gioca**. Il chiamante decide *se*
   * mostrarle (nell'area pubblica valgono fino a un'ora dal via) e passa `null`
   * quando non servono: nessun effetto sulle altre superfici.
   */
  coordinates?: { date: string; time: string; venue: string } | null
}

export default function ScorePanel({
  state,
  accentColor = '#FE7200',
  variant = 'card',
  tone = 'dark',
  className = '',
  coordinates = null,
  substitution = null,
  commentaryGate = true,
  playerNames = null,
  block = null,
  ace = null,
}: Props) {
  accentColor = state.scorebugTheme?.accent ?? accentColor
  const homeMP = isMatchPoint(state, 'home')
  const awayMP = isMatchPoint(state, 'away')
  const homeSP = !homeMP && isSetPoint(state, 'home')
  const awaySP = !awayMP && isSetPoint(state, 'away')
  // «Cosa mostrare» sta in una funzione pura: card e broadcast leggono la stessa.
  const presentation = presentMatch(state, { coordinates })

  if (variant === 'broadcast') {
    return (
      <div
        data-tone={tone}
        className={`score-panel-broadcast ${className}`}
        style={tone === 'light' ? (LIGHT_TONE_VARS as React.CSSProperties) : undefined}
      >
        <BroadcastPanel
          state={state}
          homeMP={homeMP} awayMP={awayMP}
          homeSP={homeSP} awaySP={awaySP}
        />
      </div>
    )
  }

  return (
    <div
      data-tone={tone}
      className={`score-panel-card flex flex-col rounded-xl overflow-hidden ${className}`}
      style={{
        '--accent': accentColor,
        // Proporzione fissa (5:2): l'altezza non cambia tra gli stati. Le misure
        // interne usano cqi, quindi scala con la larghezza senza breakpoint.
        aspectRatio: '5 / 2',
        containerType: 'inline-size',
        ...(tone === 'light' ? LIGHT_TONE_VARS : {}),
      } as React.CSSProperties}
    >
      <CardPanel
        state={state}
        accentColor={accentColor}
        tone={tone}
        homeMP={homeMP} awayMP={awayMP}
        homeSP={homeSP} awaySP={awaySP}
        presentation={presentation}
        substitution={substitution}
        commentaryGate={commentaryGate}
        playerNames={playerNames}
        block={block}
        ace={ace}
      />
    </div>
  )
}

// ─── Card variant ─────────────────────────────────────────────────────────────

/**
 * Corpo dei testi del tabellone «card».
 *
 * Il contenitore ha `container-type: inline-size` (lo imposta `CardPanel`), perciò
 * `cqi` = 1% della larghezza del tabellone: le misure **crescono con la larghezza
 * reale** invece di restare fisse. Il minimo è la misura pensata per il tabellone
 * stretto (~360px, telefono).
 *
 * I tetti sono tarati sul **canvas 1920** (il pannello «Scheda partita» in onda),
 * non sul tabellone del sito: con tetti da 14-22 px, su 1920 il nome restava a 22 px
 * (l'1% della larghezza) mentre i due stemmi sfumati riempivano mezza card — e la
 * scheda in onda era illeggibile. Sul sito non cambia nulla: lì il tabellone arriva
 * a ~670 px, dove `cqi` resta sotto i vecchi tetti.
 */
const CARD_FS = {
  xs: 'clamp(9px, 2.4cqi, 46px)',
  sm: 'clamp(10px, 2.6cqi, 50px)',
  md: 'clamp(11px, 2.9cqi, 56px)',
  lg: 'clamp(13px, 3.1cqi, 60px)',
  setsWon: 'clamp(11.52px, 2.7cqi, 52px)',
  /** Data, ora e palazzetto: stanno su **una riga**, quindi il corpo più piccolo. */
  xxs: 'clamp(8px, 2.1cqi, 41px)',
  /**
   * Riga dei dettagli (data · ora · palazzetto). A 8 px sulla card da 384 px non
   * si leggeva: resta su una riga sola, ma con un corpo vero.
   */
  footer: 'clamp(10.5px, 2.75cqi, 48px)',
} as const

/**
 * Passaggio fra punteggio e sostituzione, nelle due direzioni.
 *
 * Non è uno scambio secco: il blocco che lascia la scena sfuma e si rimpicciolisce
 * (170 ms), poi entra l'altro. Senza, il tabellone "saltava" da un contenuto
 * all'altro e l'occhio non capiva che era lo stesso posto.
 */
/**
 * Il commento resta in onda cinque secondi, poi sparisce anche se lo stato è
 * ancora `visible`. Il server fa lo stesso (spegne `visible` dopo 5 s): qui c'è
 * la rete di sicurezza per chi ha la pagina già aperta, così il riquadro non
 * resta appeso se il publish si perde o se la scheda era in background.
 */
function useCommentaryTtl(text: string, ttlMs: number): string {
  const [shown, setShown] = useState(text)

  useEffect(() => {
    setShown(text)
    if (!text) return
    const timer = setTimeout(() => setShown(''), ttlMs)
    return () => clearTimeout(timer)
  }, [text, ttlMs])

  return shown
}

function SwapCross({ active, swap, score, className = '' }: {
  active: boolean
  swap: ReactNode
  score: ReactNode
  /** Layout della riga: la transizione avvolge i figli, quindi deve essere flex come loro. */
  className?: string
}) {
  const [showSwap, setShowSwap] = useState(active)
  const [closing, setClosing] = useState(false)

  useEffect(() => {
    if (active === showSwap) return
    setClosing(true)
    // 300 ms: a 170 il cambio era troppo rapido per essere letto, soprattutto
    // in ingresso. La stessa durata sta nelle animazioni `.lv-cross-*`.
    const timer = setTimeout(() => {
      setShowSwap(active)
      setClosing(false)
    }, 300)
    return () => clearTimeout(timer)
  }, [active, showSwap])

  return (
    <div className={`${className} ${closing ? 'lv-cross-out' : 'lv-cross-in'}`}>
      {showSwap ? swap : score}
    </div>
  )
}

/**
 * Il velo del servizio: **un'area sola** che si sposta da una metà all'altra.
 *
 * Due gradienti affiancati dentro un contenitore largo il doppio (casa a
 * sinistra, ospiti a destra); traslandolo di metà entra l'una o l'altra. Il
 * cambio palla diventa così un movimento continuo con `ease-in-out`, non uno
 * scambio secco di sfondo fra due colonne.
 *
 * Sta **sotto** le colonne: è il primo figlio della griglia e le colonne sono
 * `relative`, quindi l'ordine di pittura lo tiene dietro nomi e punteggi.
 */
function ServiceVeil({
  serveHome,
  battutaHome,
  battutaAway,
  tone,
  hidden = false,
}: {
  serveHome: boolean
  battutaHome: string
  battutaAway: string
  tone: 'dark' | 'light'
  hidden?: boolean
}) {
  const ref = useRef<HTMLDivElement>(null)
  /** Lato mostrato all'ultimo frame: serve a sapere da dove parte il movimento. */
  const shownHome = useRef(serveHome)
  // Sul chiaro serve più corpo: la stessa tinta attenuata su bianco si perde.
  const stop = (color: string) => `color-mix(in srgb, ${color} ${tone === 'light' ? 40 : 44}%, transparent) 0%, color-mix(in srgb, ${color} ${tone === 'light' ? 16 : 14}%, transparent) 48%, transparent 74%`

  useEffect(() => {
    const el = ref.current
    const from = shownHome.current ? 0 : -50
    const to = serveHome ? 0 : -50
    shownHome.current = serveHome
    if (!el || hidden || from === to) return
    // **Web Animations API** e non una transizione CSS: `el.animate()` parte
    // sempre, anche quando React aggiorna il DOM nello stesso frame del cambio
    // (su WebKit la transizione su `transform` veniva persa e il lato saltava).
    // `Element.animate` è supportata da Safari 13.1+.
    if (typeof el.animate !== 'function') return
    el.getAnimations().forEach((animation) => animation.cancel())
    const reduce = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    if (reduce) {
      // Nessuno scivolamento: il lato cambia subito e il velo entra in dissolvenza.
      el.animate([{ opacity: 0.3 }, { opacity: 1 }], { duration: 420, easing: 'ease-out', fill: 'both' })
      return
    }
    el.animate(
      [{ transform: `translateX(${from}%)` }, { transform: `translateX(${to}%)` }],
      { duration: 1200, easing: 'ease-in-out', fill: 'both' },
    )
  }, [serveHome, hidden])

  return (
    <div aria-hidden data-service-veil className="pointer-events-none absolute inset-0 overflow-hidden">
      <div
        ref={ref}
        className="service-veil absolute inset-y-0 left-0 w-[200%]"
        data-serve={hidden ? 'off' : serveHome ? 'home' : 'away'}
        style={{
          backgroundImage: `linear-gradient(90deg, ${stop(battutaHome)}), linear-gradient(270deg, ${stop(battutaAway)})`,
          backgroundSize: '50% 100%',
          backgroundPosition: '0% 0%, 100% 0%',
          backgroundRepeat: 'no-repeat',
          transform: hidden ? 'translateX(0%)' : serveHome ? 'translateX(0%)' : 'translateX(-50%)',
          opacity: hidden ? 0 : 1,
        }}
      />
    </div>
  )
}

function TeamColumn({
  side, name, logo, score, setsWon, timeoutsUsed, hasService,
  isMatchPoint, isSetPoint, accentColor, displayMode = 'score', theme, tone = 'dark',
  showBigNumber = true, showSets = true, winner = false, isVoid = false, swap = null,
  block = false,
  ace = null,
}: {
  side: 'home' | 'away'
  name: string; logo?: string; score: number
  setsWon: number; timeoutsUsed: number; hasService: boolean
  isMatchPoint: boolean; isSetPoint: boolean
  /** Sostituzione di questa squadra: sostituisce la riga del punteggio. */
  swap?: SubstitutionSwap | null
  accentColor: string
  displayMode?: 'score' | 'final-sets'
  theme?: ScorebugThemeConfig
  /** Tono del tabellone: decide quanto si vede lo stemma di sfondo (vedi sotto). */
  tone?: 'light' | 'dark'
  /** Falso prima del via e sulle gare rinviate/annullate: al posto del numero, «–». */
  showBigNumber?: boolean
  /** Falsa quando set vinti e timeout non hanno senso (prima del via, annullata…). */
  showSets?: boolean
  /** Squadra vincente: nome e numero in verde + segno ✓ nello spazio del servizio. */
  winner?: boolean
  /** Gara senza esito (rinviata/annullata): nomi attenuati. */
  isVoid?: boolean
  /** Muro appena fatto da questa squadra: overlay dei mattoni. */
  block?: boolean
  /**
   * Ace in onda su questa metà: `score` = la segna (qui compare la scritta),
   * `concede` = la subisce (qui batte il pallone).
   */
  ace?: 'score' | 'concede' | null
}) {
  const isRight = side === 'away'
  const isFinalSets = displayMode === 'final-sets'
  // Colori propri di questa squadra: se non impostati valgono quelli generali.
  const own = side === 'home'
    ? { text: theme?.homeText, logoBg: theme?.homeLogoBg, serviceColor: theme?.homeServiceColor }
    : { text: theme?.awayText, logoBg: theme?.awayLogoBg, serviceColor: theme?.awayServiceColor }
  // ── Guardia di contrasto ────────────────────────────────────────────────
  // Il colore societario può essere un blu notte: usato com'è sul fondo della
  // colonna (`mainBg`) il numero della squadra che serve sparisce (misurato:
  // #0b2b5b su #0d0d1a = 1,39:1, sotto ogni soglia). `readableOnDark` schiarisce
  // la **stessa tinta** fino al rapporto richiesto; per un colore non hex (var(),
  // rgb()) resta invariato, quindi i temi esistenti non cambiano.
  const columnBg = theme?.mainBg ?? (tone === 'light' ? '#eef3fa' : '#171e2e')
  // Il fondo reale del tabellone scuro è `--sb-main-bg` = #171e2e (non #0d0d1a):
  // con il default sbagliato la guardia si fermava a 2,6:1 misurati invece di 3:1.
  // Sul tema chiaro il fondo è una gradiente bianco→#eef3fa: per la guardia si
  // usa l'estremo più scuro (quello che dà il rapporto peggiore).
  const toneFallbackBg = tone === 'light' ? '#eef3fa' : '#171e2e'
  const bgForContrast = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(columnBg) ? columnBg : toneFallbackBg
  /**
   * Stesso colore, leggibile sul fondo della colonna **di questo tema**.
   * Sul chiaro si scurisce, sul scuro si schiarisce: una guardia sola non basta,
   * perché schiarire un bianco lo lascia bianco (era il difetto delle card chiare
   * di `/live`, nomi bianchi su fondo bianco).
   */
  const readable = (color: string, minRatio: number) => (tone === 'light'
    ? readableOnLight(color, bgForContrast, minRatio)
    : readableOnDark(color, bgForContrast, minRatio))
  const testo = readable(own.text ?? theme?.text ?? (tone === 'light' ? '#162133' : '#ffffff'), 4.5)
  const sfondoLogo = own.logoBg ?? theme?.logoBg
  // Numero grande (testo ≥ 24px): soglia AA per testo grande, 3:1.
  // Il numero tiene **sempre** la tinta della squadra: a renderlo leggibile è
  // il bordo, non un colore sostitutivo. Il bordo è bianco sulle tinte scure e
  // quasi nero su quelle chiare, cioè sempre quello che contrasta di più.
  const battuta = readable(own.serviceColor ?? theme?.serviceColor ?? accentColor, 2.4)
  const battutaLum = relativeLuminance(battuta) ?? 0.5
  const numeroStroke = battutaLum < 0.45 ? '#ffffff' : '#0b0b12'
  const swapActive = !!swap && !isFinalSets
  const scoreColor = isFinalSets ? testo : hasService ? battuta : testo
  // L'esito si vede **senza righe in più**: colore e segno sullo spazio già esistente.
  const nameColor = winner ? 'var(--sb-win, #4ade80)' : isVoid ? 'var(--sb-text-soft, rgba(255,255,255,0.55))' : testo
  // In match point il numero passa al verde e pulsa: è il punto che chiude la
  // partita, e deve distinguersi dal set point (che illumina il cerchio).
  const numberColor = winner || isMatchPoint ? 'var(--sb-win, #4ade80)' : scoreColor

  return (
    <div
      data-section={side}
      // Durante una scena tutto il contenuto della metà (nome, punteggio, set,
      // timeout) va quasi trasparente: la regola sta in `globals.css` e
      // **esclude** gli overlay delle scene, che devono restare pieni.
      data-scene-under={block || ace ? 'true' : undefined}
      className={`relative overflow-hidden flex flex-col justify-center gap-0.5 px-2 py-1 ${isRight ? 'items-end' : 'items-start'}`}
      // Il velo del servizio non sta più sulla colonna: è un'area sola che si
      // sposta da una metà all'altra (vedi `ServiceVeil`).
      // Il divisore è **solo** sul bordo interno della colonna ospite: sulla
      // colonna di casa un `border-left` disegnava una riga chiara sul fianco
      // sinistro della card, senza nulla a cui servire.
      style={{
        borderLeft: isRight ? '1px solid var(--sb-border-color, rgba(255,255,255,0.16))' : 'none',
      }}
    >
      {/* ── Faded logo background ────────────────────────────────────────────── */}
      {logo && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          // Con `scale(3.12)` lo stemma viene disegnato a ~571 px CSS: a DPR 2
          // servono ~1150 px di sorgente, altrimenti il browser ingrandisce un
          // bitmap più piccolo. Il downscaling resta lato server (Lanczos).
          src={fipavLogoProxyUrl(logo, 1152)!}
          alt=""
          aria-hidden
          className="absolute inset-0 w-full h-full object-contain pointer-events-none select-none"
          style={{
            // Sul tono chiaro lo stemma al 35% è una filigrana; sul tono scuro lo stesso
            // 35% su un fondo quasi nero diventa l'elemento dominante e copre nomi e
            // punteggio (era il difetto della «Scheda partita» in onda). Più tenue, e
            // senza il `scale(1.3)` che lo faceva sbordare oltre la propria colonna.
            // Col velo di servizio il fondo della colonna si alza di tono: lo
            // stemma al 10% spariva. Sale al 24% solo quando la colonna è
            // illuminata, restando una filigrana (mai sopra nomi e punteggio).
            // Sulla colonna che serve lo stemma è più presente e ruotato in
            // prospettiva **dall'esterno verso l'interno**, dove l'interno è il
            // centro del tabellone: il bordo esterno va in profondità e il bordo
            // verso il centro resta vicino, quindi lo stemma cresce andando verso
            // il centro. Il segno cambia col lato.
            // Lo stemma non dipende dal servizio: è parte della squadra, non un
            // indicatore di battuta (quello è il velo). Stessa opacità e stessa
            // prospettiva su tutte e due le colonne, sempre.
            // Sotto una scena (mattoni o ace) lo stemma scende a un decimo del
            // suo valore: con l'opacità scritta inline la regola della colonna
            // non lo toccava, e l'esplosione dell'ace finiva sopra un logo a
            // piena forza.
            opacity: (tone === 'light' ? 0.45 : 0.28) * (block || ace ? 0.1 : 1),
            // Nessun filtro **qui**: la rimozione dello sfondo e il
            // ridimensionamento (Lanczos) li fa il proxy sulla sorgente, a piena
            // risoluzione, prima che l'immagine arrivi come sfondo. Un `filter`
            // CSS su un'immagine già in secondo piano veniva applicato dopo la
            // composizione, e a scala ridotta diventava un alone che impastava
            // il disegno.
            // Rotazione come i badge delle card partita (`.league-watermark`),
            // angolo ridotto di un terzo (30 -> 21 gradi) e ancorata al bordo
            // esterno (10% di traslazione in fuori). La prospettiva è 700 e non
            // 300: con scala 1,9 e i 300 px del watermark la proiezione del lato
            // vicino esplodeva (box 966 px, l'intera card).
            // Identico nei due temi: il tema cambia solo opacità e blend. Prima
            // il chiaro aggiungeva un `scale(1.3)`, per cui lo stemma era più
            // grande e spostato rispetto allo scuro.
            // Casa e ospite sono speculari rispetto al centro del tabellone:
            // stessa traslazione (70%) e stessa rotazione (0,2°) con segno
            // invertito, così lo stemma "esce" verso il bordo esterno su
            // entrambe le colonne. Valori indicati dall'operatore.
            // Geometria scelta dall'operatore: `translateX(±102%)` porta lo
            // stemma fuori dalla colonna, `scale(3.12)` lo ingrandisce e la
            // prospettiva da 500 px dà profondità senza deformare. `rotateY(0°)`
            // è esplicito: la rotazione non serve, la profondità la dà la
            // prospettiva. Casa è lo specchio esatto dell'ospite.
            // Rotazione 3D verso l'esterno: ora si può, perché non c'è più il
            // blend a degradare il disegno.
            transform: `translateX(${isRight ? 102 : -102}%) perspective(500px) rotateY(${isRight ? -14 : 14}deg) scale(3.12)`,
            transformOrigin: isRight ? 'right center' : 'left center',
            background: sfondoLogo ?? 'var(--sb-logo-bg, transparent)',
          }}
        />
      )}

      {/* ── Row 1: team name ─────────────────────────────────────────────────── */}
      {/* Row 1: due righe **riservate** al nome (che si manda a capo invece di troncarsi:
          un «…» taglierebbe proprio il nome della squadra). La riserva è uguale nelle due
          colonne, così i due punteggi restano alla stessa altezza, e la misura del testo
          è una sola per tutti i nomi della card. */}
      <div
        className={`relative flex w-full min-w-0 items-center gap-1 ${isRight ? 'flex-row-reverse' : 'flex-row'}`}
        style={{
          fontSize: 'clamp(10px, 3.4cqi, 66px)',
          minHeight: '1.3em',
          // Il badge SP/MP sta appeso sotto il nome (absolute top-full): senza
          // questa riserva la coda del nome gli finisce sotto. Solo sul lato
          // interno, dove il badge e' ancorato.
          ...((isMatchPoint || isSetPoint) && !isFinalSets && showBigNumber
            ? { [isRight ? 'paddingLeft' : 'paddingRight']: 'clamp(34px, 11cqi, 140px)' }
            : {}),
        }}
      >
        {winner && (
          <span
            aria-hidden
            className="flex-shrink-0 font-display text-[13px] font-bold leading-none"
            style={{ color: 'var(--sb-win, #4ade80)', fontSize: CARD_FS.lg }}
          >
            ✓
          </span>
        )}
        {/* Un nome lungo deve restare su **una** riga: il tabellone non può
            crescere in altezza (aspect 5:2) e un nome a capo comprimerebbe
            numero e parziali. Ellissi + nome completo nel title. */}
        <span
          className="min-w-0 truncate font-display font-bold uppercase leading-[1.05]"
          style={{ color: nameColor }}
          title={name}
        >
          {name}
        </span>
        {(isMatchPoint || isSetPoint) && !isFinalSets && showBigNumber && !swapActive && (
          <span
            className={`absolute top-full mt-0.5 ${isRight ? 'left-0' : 'right-0'} font-bold uppercase rounded z-[2] flex flex-col items-center justify-center gap-0 ${
              isMatchPoint ? '' : 'border border-yellow-400/60 bg-black/70 text-yellow-300'
            }`}
            style={{
              fontSize: 'clamp(6px, 2.1cqi, 18px)',
              lineHeight: 1,
              paddingInline: 'clamp(2px, 0.9cqi, 8px)',
              paddingBlock: 'clamp(1px, 0.5cqi, 4px)',
              ...(isMatchPoint ? { background: 'var(--sb-accent-strong, #FE7200)', color: 'var(--sb-secondary-text, #ffffff)' } : {}),
            }}
          >
            <span>{isMatchPoint ? 'MATCH' : 'SET'}</span>
            <span>POINT</span>
          </span>
        )}
      </div>


      {/* ── Row 2: score + sets won ───────────────────────────────────────────── */}
      <div className="relative">
        <SwapCross
          active={swapActive}
          className={`flex gap-2 ${swapActive ? 'w-full items-center justify-center' : 'items-end'} ${isRight ? 'flex-row-reverse' : 'flex-row'}`}
          swap={swapActive && swap ? (

          // Sostituzione in corso: nello spazio squadre, al posto di punti,
          // set, timeout e badge, i due numeri. Il resto del tabellone — stato,
          // nomi, parziali — non si muove.
          <div
            data-section="score"
            className="lv-swap relative"
            aria-label={`Sostituzione: esce ${swap.out ?? '—'}, entra ${swap.in ?? '—'}`}
          >
            <span
              className="lv-swap-circle lv-swap-out font-display font-bold tabular-nums"
              style={{ color: '#f05252' }}
            >
              {swap.out ?? '—'}
            </span>
            <span
              className="lv-swap-circle lv-swap-in font-display font-bold tabular-nums"
              style={{ color: '#34d399' }}
            >
              {swap.in ?? '—'}
            </span>
          </div>
          ) : null}
          score={
            <>
            {/* Score */}
            <div className="relative">
              {isFinalSets && (
                <span className={`absolute -top-4 ${isRight ? 'right-0' : 'left-0'} text-[9px] font-bold uppercase tracking-[0.2em] text-white/45`} style={{ fontSize: CARD_FS.xs }}>
                  Set
                </span>
              )}
              {/*
                Casella a **larghezza fissa di due cifre**, con il numero
                centrato: il set non deve muoversi quando il punteggio passa da
                una cifra a due.

                La larghezza non si prenota con `2ch`: `ch` è l'avanzo dello `0`
                **proporzionale** (43 px qui) mentre le cifre sono `tabular-nums`
                (47 px), quindi `2ch` restava più stretto di "12" e la casella
                cresceva lo stesso. Si prenota invece con un fantasma a due
                cifre, invisibile e non annunciato: stesso font, stesso
                `tabular-nums`, quindi è **esattamente** largo quanto qualunque
                coppia di cifre. Il numero vero sta sopra, in `absolute`, così
                non partecipa al layout e resta al centro.
              */}
              <span
                data-score-slot
                className="relative inline-flex items-center justify-center font-display font-bold tabular-nums"
                style={{ fontSize: 'clamp(26px, 14cqi, 176px)' }}
              >
                <span
                className={`absolute inset-0 flex items-center justify-center font-display font-bold tabular-nums leading-none transition-all duration-150${isMatchPoint ? ' match-point-score' : ''}`}
                // Ripristinati i valori del 20/10 (prima dei ritocchi di oggi): il numero
            // era il 7% della larghezza, non il 14%. Il 14cqi lo raddoppiava.
            // 10cqi: il 14 raddoppiava (card troppo piena), il 7 lasciava la card
            // vuota e il punteggio ridotto a un trattino. 10cqi corrisponde
            // alla proporzione del tabellone di test (42px su 398 = 10,5%).
            style={{
              fontSize: 'inherit',
              color: numberColor,
              // Bordo invece di riempimento bianco: il numero resta della
              // squadra e si stacca dal logo/fondo che c'è dietro. Lo spessore
              // è proporzionale al glifo: 0,055em ≈ 3 px a 54 px (il tratto CSS
              // è centrato sul bordo, quindi metà va fuori e metà dentro).
              WebkitTextStroke: `0.055em ${numeroStroke}`,
              paintOrder: 'stroke fill',
              textShadow: '0 1px 3px rgba(0,0,0,0.35)',
            } as React.CSSProperties}
              >
                {showBigNumber ? score : '–'}
              </span>
                {/* Il fantasma viene **dopo** il numero, non prima: è
                    `aria-hidden` e invisibile, ma resta un `span` con
                    `tabular-nums`, e chi legge `[data-section="home"]
                    span.tabular-nums` (test, diagnosi) prende il **primo**
                    elemento: deve essere il numero vero, non il segnaposto. */}
                <span aria-hidden className="invisible leading-none">88</span>
              </span>
            </div>

            {/* Sets won badge */}
            {!isFinalSets && showSets && (
              // Pallini dei timeout **a lato** del set, non sotto: lo spazio
              // verticale che liberano va al set, che può crescere (il numero del
              // punto è più alto di lui, quindi la riga non si allunga).
              <div
                data-section="score"
                className={`flex items-center gap-1.5 pb-1 ${isRight ? 'flex-row-reverse' : 'flex-row'}`}
              >
                <span
                  className={`inline-flex items-center justify-center rounded-full border font-display font-bold leading-none${isSetPoint ? ' set-point-glow' : ''}${isMatchPoint ? ' match-point-glow' : ''}`}
                  style={{
                    width: 'clamp(30px, 11cqi, 150px)',
                    height: 'clamp(30px, 11cqi, 150px)',
                    borderColor: theme?.accentStrong ?? theme?.chipBorder ?? readable(accentColor, 3),
                    color: theme?.secondaryText ?? (tone === 'light' ? 'var(--sb-text, #162133)' : 'var(--sb-secondary-text, #ffffff)'),
                    background: 'var(--sb-sets-bg, rgba(255,255,255,0.08))',
                    // La cifra riempie il cerchio: prima stava al 51% del
                    // diametro e sembrava un dettaglio secondario. 7cqi su un
                    // cerchio da 11cqi è ~64%, con il margine per il bordo.
                    fontSize: 'clamp(18px, 7cqi, 100px)',
                  }}
                >
                  {setsWon}
                </span>

                <div className="flex flex-col gap-1">
                  {Array.from({ length: 2 }, (_, i) => (
                    <span
                      key={i}
                      className="rounded-full"
                      style={{
                        width: 'clamp(8px, 3.2cqi, 52px)',
                        height: 'clamp(8px, 3.2cqi, 52px)',
                        // Bordo **più spesso** e colore calcolato sul tema: nel
                        // chiaro il ripiego era bianco al 32% su fondo bianco
                        // (invisibile). Minimo 2 px perché il browser tronca i
                        // bordi a pixel interi: con un minimo di 1,5 px restava
                        // 1 px sulle card piccole. 0,5cqi ≈ 2 px a card 400,
                        // ≈3 px a 672.
                        borderStyle: 'solid',
                        borderWidth: 'clamp(2px, 0.5cqi, 8px)',
                        boxSizing: 'border-box',
                        borderColor: i < timeoutsUsed
                          ? numeroStroke
                          : (tone === 'light' ? 'rgba(22,33,51,0.55)' : 'rgba(255,255,255,0.42)'),
                        backgroundColor: i < timeoutsUsed ? battuta : 'transparent',
                      }}
                      title={i < timeoutsUsed ? 'Timeout usato' : 'Timeout disponibile'}
                    />
                  ))}
                </div>
              </div>
            )}

            </>
          }
        />
      </div>

      {/* Le scene stanno **dopo** il contenuto della colonna: montate prima,
          finivano sotto il velo dello stemma (che è un fratello successivo) e
          l'esplosione dell'ace si vedeva a malapena. */}
      {block && <MonsterBlockScene side={side} />}
      {/* Due metà, due ruoli: il pallone cade dove l'ace lo subisce, la scritta
          compare dove lo si segna. */}
      {ace === 'concede' && <AceScene side={side} accent={accentColor} variant="landing" />}
      {ace === 'score' && <AceScene side={side} accent={accentColor} variant="label" />}
    </div>
  )
}

function CardPanel({
  state, accentColor, tone,
  homeMP, awayMP, homeSP, awaySP, presentation, substitution = null, commentaryGate = true,
  playerNames = null,
  block = null,
  ace = null,
}: {
  state: MatchState; accentColor: string
  /** Fondo del tabellone: serve ai colori di stato (`MatchStatus`), non al layout. */
  tone: 'dark' | 'light'
  homeMP: boolean; awayMP: boolean; homeSP: boolean; awaySP: boolean
  presentation: BoardPresentation
  substitution?: SubstitutionSwap | null
  /**
   * Il commento va in onda solo **dopo** il punto attribuito dallo scorer. Chi ha
   * gli eventi (`useLiveSubstitution`) lo dice; senza, vale `true` (anteprima,
   * pagina pubblica), dove non c'è un flusso di eventi da guardare.
   */
  commentaryGate?: boolean
  playerNames?: { home: Record<string, string>; away: Record<string, string> } | null
  /** Muro: la metà della squadra che l'ha fatto va in scena. */
  block?: { team: LiveSide } | null
  /** Ace: la scena va sulla metà di chi lo subisce. */
  ace?: { team: LiveSide } | null
}) {
  const leagueLabel = state.leagueShort ?? null
  // Riga di commento: solo con il pannello acceso e un testo non vuoto.
  // «Solo dopo il punto»: acceso ma non ancora seguito da `point_scorer`, il
  // commento resta spento. Poi resta in onda cinque secondi.
  const commentarySource = state.commentary?.visible === true && commentaryGate
    ? (state.commentary.text ?? '').trim()
    : ''
  const commentaryText = useCommentaryTtl(commentarySource, 5000)
  const showFinalSets = state.isMatchFinished || state.status === 'finished'

  // La riga dei parziali esiste anche quando i parziali non ci sono ancora: in
  // riscaldamento e in gara la striscia deve esserci (con i posti vuoti), o il
  // footer si abbassa e la card "salta".
  const liveOrWarmup = ['warmup', 'live', 'pause', 'timeout_home', 'timeout_away'].includes(state.status)
  // Stessa guardia del numero, soglia più bassa: il velo è un fondo colorato
  // attenuato, non un testo.
  const veilBgRaw = state.scorebugTheme?.mainBg
  const veilBg = veilBgRaw && /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(veilBgRaw)
    ? veilBgRaw
    : (tone === 'light' ? '#ffffff' : '#171e2e')
  // Sul **chiaro** il velo va scurito, non schiarito: un giallo come `#e8c547`
  // al 22% su bianco è quasi invisibile (era il difetto: chi serve non si
  // distingueva). Sul scuro resta la guardia che schiarisce.
  const veilColor = (color: string) => (tone === 'light'
    ? readableOnLight(color, veilBg, 2.6)
    : readableOnDark(color, veilBg, 3))
  // Parziali: set conclusi **e** quello in corso (vedi lib/live/partials.ts).
  const partials = displayPartials(state, 5).filter((set): set is NonNullable<typeof set> => set !== null)
  const homeLogo = state.homeTeamLogo ?? undefined
  const awayLogo = state.awayTeamLogo ?? undefined

  const winner = winningSide(state)
  const isVoid = presentation.emphasis === 'void'
  // Cornice: verde a partita conclusa con vincitore, rossa se annullata, neutra altrimenti.
  const frameColor = state.status === 'cancelled'
    ? 'var(--sb-loss-border, rgba(248,113,113,0.5))'
    : winner
      ? 'var(--sb-win-border, rgba(74,222,128,0.55))'
      : 'var(--sb-border-color, rgba(255,255,255,0.16))'

  // Inchiostro e bordo del chip del campionato, calcolati sul fondo reale.
  const chipInk = tone === 'light'
    ? readableOnLight(state.scorebugTheme?.secondaryText ?? accentColor, '#ffffff', 4.5)
    : (state.scorebugTheme?.secondaryText ?? state.scorebugTheme?.textSoft ?? 'rgba(255,255,255,0.8)')
  const chipBorder = state.scorebugTheme?.centerBorder
    ?? state.scorebugTheme?.chipBorder
    ?? (tone === 'light' ? `${accentColor}66` : 'rgba(255,255,255,0.15)')

  return (
    <div
      data-section="board"
      data-phase={presentation.phase}
      data-status={state.status}
      className="relative flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border"
      style={{ background: 'var(--sb-main-bg, #171e2e)', borderColor: frameColor }}
    >
      {/* Accent bar */}
      <div className="h-0.5 w-full" style={{ backgroundColor: accentColor }} />

      {/* Status bar */}
      {/* Chip del campionato: il colore di ripiego era bianco anche sul tema
          chiaro, dove il fondo è una tinta chiara dell'accento → invisibile.
          Ora l'inchiostro è calcolato sul tema: sul chiaro è l'accento scurito
          fino a 4,5:1, sul scuro resta il bianco attenuato. */}
      <div data-section="score" className="flex flex-none items-center justify-between px-3 py-1 border-b" style={{ borderColor: 'var(--sb-border-color, rgba(255,255,255,0.06))' }}>
        <MatchStatus
          status={state.status}
          isTiebreak={state.isTiebreak}
          currentSet={state.currentSet}
          fontSize={CARD_FS.md}
          tone={tone}
          overrideLabel={substitution ? 'Sostituzione' : null}
        />
        {leagueLabel && (
          <span
            data-section="center"
            className="font-display font-bold uppercase tracking-widest text-[9px] px-2 py-0.5 rounded-full border"
            style={{
              fontSize: CARD_FS.xs,
              background: state.scorebugTheme?.centerSlotBg ?? accentColor + '30',
              color: chipInk,
              borderColor: chipBorder,
            }}
          >
            {leagueLabel}
          </span>
        )}
      </div>

      {/* Two-column grid */}
      <div className="relative grid min-h-0 flex-1 grid-cols-2 divide-x divide-white/6">
        <ServiceVeil
          serveHome={state.serviceTeam === 'home'}
          // Il velo è già attenuato (36%/10% di alpha): basta che il colore
          // stia sopra 2:1, altrimenti con un accent scuro il servizio non si
          // vede più su nessuna delle due metà.
          battutaHome={veilColor(state.scorebugTheme?.homeServiceColor ?? state.scorebugTheme?.serviceColor ?? accentColor)}
          battutaAway={veilColor(state.scorebugTheme?.awayServiceColor ?? state.scorebugTheme?.serviceColor ?? accentColor)}
          tone={tone}
          // `!state.serviceTeam`: senza servizio impostato non si illumina nessuna
          // metà (prima, con `serviceTeam` null, il velo cadeva sull'ospite).
          hidden={showFinalSets || !state.serviceTeam}
        />
        <TeamColumn
          side="home"
          name={state.homeTeam}
          logo={homeLogo}
          score={showFinalSets ? state.setsWon.home : state.currentScore.home}
          setsWon={state.setsWon.home}
          timeoutsUsed={state.timeoutsUsed.home}
          hasService={!showFinalSets && state.serviceTeam === 'home'}
          isMatchPoint={showFinalSets ? false : homeMP}
          isSetPoint={showFinalSets ? false : homeSP}
          accentColor={accentColor}
          tone={tone}
          displayMode={showFinalSets ? 'final-sets' : 'score'}
          theme={state.scorebugTheme}
          showBigNumber={presentation.showBigNumber}
          showSets={presentation.showSets}
          winner={winner === 'home'}
          isVoid={isVoid}
          block={block?.team === 'home'}
          ace={ace ? (ace.team === 'home' ? 'score' : 'concede') : null}
          swap={substitution?.team === 'home' ? substitution : null}
        />
        <TeamColumn
          side="away"
          name={state.awayTeam || '—'}
          logo={awayLogo}
          score={showFinalSets ? state.setsWon.away : state.currentScore.away}
          setsWon={state.setsWon.away}
          timeoutsUsed={state.timeoutsUsed.away}
          hasService={!showFinalSets && state.serviceTeam === 'away'}
          isMatchPoint={showFinalSets ? false : awayMP}
          isSetPoint={showFinalSets ? false : awaySP}
          accentColor={accentColor}
          tone={tone}
          displayMode={showFinalSets ? 'final-sets' : 'score'}
          theme={state.scorebugTheme}
          showBigNumber={presentation.showBigNumber}
          showSets={presentation.showSets}
          winner={winner === 'away'}
          isVoid={isVoid}
          block={block?.team === 'away'}
          ace={ace ? (ace.team === 'away' ? 'score' : 'concede') : null}
          swap={substitution?.team === 'away' ? substitution : null}
        />
          {/* Commento in onda: un riquadro **semi opaco** nell'area centrale del
              tabellone, sopra la riga dei numeri. Sta in `absolute` e non in
              flusso: nulla si sposta quando il commento si accende o si spegne.
              Tre righe al massimo (lo stesso limite che il reducer impone al
              testo). */}
          {commentaryText !== '' && (
            <div
              data-section="commentary"
              className="lv-comment font-display"
              style={{ ['--lv-comment-accent' as string]: accentColor }}
            >
              <span className="lv-comment-text">{commentaryText}</span>
            </div>
          )}
      </div>

      {/* ── Riga dettagli: **una sola resa per stato** (`presentMatch`) ────────── */}
      {/*     programmata → coordinate · riscaldamento → coordinate · pausa/timeout */}
      {/*     → motivo · conclusa → parziali · rinviata/annullata → motivo. */}
      <div data-section="sets" className={`flex flex-none items-center overflow-hidden px-3 py-1.5 border-t ${showFinalSets ? 'justify-center' : 'gap-2'}`} style={{ borderColor: 'var(--sb-border-color, rgba(255,255,255,0.06))', background: 'var(--sb-details-bg, transparent)' }}>
        {/* Sostituzione in onda: il piè dice **chi** esce e chi entra, non solo
            due numeri nello spazio squadre. Il nome compare solo se la distinta
            lo ha (altrimenti si resta sul numero). */}
        {substitution && (
          <span
            className="flex w-full min-w-0 items-center justify-center gap-x-1.5"
            style={{ fontSize: CARD_FS.footer, lineHeight: 1, color: 'var(--sb-text, rgba(255,255,255,0.92))' }}
          >
            <span className="shrink-0 font-display font-bold uppercase tracking-widest text-[color:var(--sb-text-soft,rgba(255,255,255,0.72))]">
              Cambio
            </span>
            <span className="min-w-0 truncate font-display font-bold uppercase">
              esce {substitution.out ?? '—'}
              {(playerNames?.[substitution.team]?.[substitution.out ?? ''] ?? substitution.outName)
                ? ` ${playerNames?.[substitution.team]?.[substitution.out ?? ''] ?? substitution.outName}`
                : ''}
            </span>
            <span className="shrink-0 opacity-40">→</span>
            <span className="min-w-0 truncate font-display font-bold uppercase">
              entra {substitution.in ?? '—'}
              {(playerNames?.[substitution.team]?.[substitution.in ?? ''] ?? substitution.inName)
                ? ` ${playerNames?.[substitution.team]?.[substitution.in ?? ''] ?? substitution.inName}`
                : ''}
            </span>
          </span>
        )}
        {!substitution && presentation.details.kind === 'coordinates' && (
          /* Data, ora e palazzetto stanno su **una riga sola**: mandandone una a capo la
             card cresce e nel riquadro scalato risulta diversa dalle altre. Per starci
             usano il corpo minore (`CARD_FS.xs`); il palazzetto, se davvero non ci
             stesse, si accorcia con i puntini (il nome intero è nel tooltip). */
          <div
            className="flex w-full min-w-0 flex-nowrap items-center justify-center gap-x-1.5"
            // `fontSize`/`lineHeight` sul **contenitore**, non solo sulle scritte: senza,
            // il line-box della riga resta quello del corpo ereditato (16px) e il piè di
            // questa card viene più alto di quello delle concluse, dove il piè sono le
            // pastiglie dei parziali.
            style={{ color: 'var(--sb-text, rgba(255,255,255,0.92))', fontSize: CARD_FS.footer, lineHeight: 1 }}
          >
            <span className="shrink-0 font-display font-bold tabular-nums leading-none" style={{ fontSize: CARD_FS.footer }}>{presentation.details.date}</span>
            <span className="shrink-0 opacity-40">·</span>
            <span className="shrink-0 font-display font-bold tabular-nums leading-none" style={{ fontSize: CARD_FS.footer }}>{presentation.details.time}</span>
            {presentation.details.venue && (
              <>
                <span className="shrink-0 opacity-40">·</span>
                <span className="flex min-w-0 flex-1 items-center justify-center gap-1">
                  <svg className="h-3.5 w-3.5 shrink-0 opacity-90" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
                  </svg>
                  <span
                    className="truncate font-display font-bold uppercase tracking-wide leading-none"
                    style={{ fontSize: CARD_FS.footer }}
                    title={presentation.details.venue}
                  >
                    {presentation.details.venue}
                  </span>
                </span>
              </>
            )}
          </div>
        )}
        {/* Il motivo (timeout/pausa) resta solo finché non ci sono parziali: appena
            ne esistono si mostrano **sempre**, anche con la gara in pausa o in
            timeout — come nella variante broadcast. Prima sparivano per tutta la
            durata della pausa/del timeout. */}
        {presentation.details.kind === 'note' && state.setHistory.length === 0 && (
          <span className="w-full text-center font-display font-bold uppercase tracking-widest text-[10px]" style={{ fontSize: CARD_FS.sm, color: 'var(--sb-text-soft, rgba(255,255,255,0.6))' }}>
            {presentation.details.text}
          </span>
        )}
        {/* `none` = questo stato **non ha parziali** (gara programmata o in
            riscaldamento senza coordinate, partita senza set registrati): prima
            finiva nello stesso ramo dei parziali e stampava «PARZIALI —» su una
            gara che non era ancora cominciata. Ora la riga resta vuota. */}
        {/* Durante il cambio il piè mostra **solo** la sostituzione: i parziali
            tornano appena la scena finisce. */}
        {!substitution && (partials.length > 0 || liveOrWarmup) && (
          <>
            {!showFinalSets && (
              // Etichetta allineata alle cifre dei parziali: prima era più piccola
              // dei numeri che introduce e quasi trasparente (0,25 di opacità).
              <span
                className="shrink-0 font-display font-bold uppercase tracking-widest"
                style={{ fontSize: CARD_FS.md, color: 'var(--sb-text-soft, rgba(255,255,255,0.72))' }}
              >
                Parziali
              </span>
            )}
            <div className="flex items-center gap-1.5">
              {partials.map((set, i) => {
                // Il set in corso non è «vinto» da nessuno: i due numeri restano
                // leggibili e la pillola si distingue col colore d'accento.
                const homeWon = !set.current && set.home > set.away
                const awayWon = !set.current && set.away > set.home
                return (
                  <span
                    key={i}
                    className="font-display font-bold tabular-nums text-[11px] leading-none px-2 py-0.5 rounded-full border whitespace-nowrap"
                    style={{
                      fontSize: CARD_FS.md,
                      paddingInline: 'clamp(8px, 1.8cqi, 14px)',
                      borderColor: set.current ? accentColor : 'var(--sb-chip-border, rgba(255,255,255,0.18))',
                      background: 'var(--sb-chip-bg, rgba(255,255,255,0.05))',
                    }}
                  >
                    <span style={{ color: homeWon || set.current ? 'var(--sb-text, #ffffff)' : 'var(--sb-text-muted, rgba(255,255,255,0.35))' }}>{set.home}</span>
                    <span className="mx-0.5" style={{ color: 'var(--sb-text-muted, rgba(255,255,255,0.2))' }}>–</span>
                    <span style={{ color: awayWon || set.current ? 'var(--sb-text, #ffffff)' : 'var(--sb-text-muted, rgba(255,255,255,0.35))' }}>{set.away}</span>
                  </span>
                )
              })}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

// ─── Broadcast variant (OBS) ──────────────────────────────────────────────────

function BroadcastPanel({
  state,
  homeMP, awayMP, homeSP, awaySP,
}: {
  state: MatchState
  homeMP: boolean; awayMP: boolean; homeSP: boolean; awaySP: boolean
}) {
  const showFinalSets = state.isMatchFinished || state.status === 'finished'

  return (
    <div
      className="w-full h-full flex flex-col"
      style={{ fontFamily: 'var(--font-display, sans-serif)' }}
    >
      {/* Top bar: competizione */}
      <div
        data-section="center"
        className="flex items-center justify-between px-6 py-2"
        /* Superficie **scura** di default, non l'accento: con la barra arancio il
           testo bianco stava a 2,76:1 e il «In corso» rosso a 1,37:1, cioè
           illeggibili in TV (misurati sui pixel). L'accento resta come bordo
           inferiore: la marca si vede, il testo si legge. */
        style={{ backgroundColor: state.scorebugTheme?.detailsBg ?? 'rgba(7, 13, 24, 0.86)', borderBottom: '2px solid var(--sb-details-accent, var(--sb-accent, transparent))' }}
      >
        <span className="px-2 py-0.5 rounded" style={{ fontSize: 18, color: 'var(--sb-secondary-text, #ffffff)', background: 'var(--sb-center-slot-bg, transparent)' }}>
          {state.leagueShort ?? 'LIVE'}
        </span>
        <MatchStatus
          status={state.status}
          isTiebreak={state.isTiebreak}
          currentSet={state.currentSet}
          className="text-white/80"
          fontSize="18px"
        />
        <div data-section="sets"><SetHistory history={state.setHistory} maxSets={5} /></div>
      </div>

      {/* Main area */}
      <div data-section="board" className="flex-1 flex items-center justify-between px-8 py-4" style={{ borderTop: '1px solid var(--sb-border-color, transparent)', background: state.scorebugTheme?.mainBg || '#0d0d1a' }}>
        {/* Casa */}
        <div className="flex-1 flex flex-col items-start gap-3">
          <TeamBlock
            side="home"
            name={state.homeTeam}
            logo={state.homeTeamLogo}
            setsWon={state.setsWon.home}
            timeoutsUsed={state.timeoutsUsed.home}
            hasService={!showFinalSets && state.serviceTeam === 'home'}
            setsDisplay={showFinalSets ? 'number' : 'dots'}
            theme={state.scorebugTheme}
          />
        </div>

        {/* Score centrale */}
        <div data-section="score">
        <ScoreBlock
          homeScore={showFinalSets ? state.setsWon.home : state.currentScore.home}
          awayScore={showFinalSets ? state.setsWon.away : state.currentScore.away}
          serviceTeam={showFinalSets ? null : state.serviceTeam}
          isMatchPoint={showFinalSets ? undefined : { home: homeMP, away: awayMP }}
          isSetPoint={showFinalSets ? undefined : { home: homeSP, away: awaySP }}
          displayMode={showFinalSets ? 'sets' : 'score'}
          className="text-8xl px-8"
          theme={state.scorebugTheme}
        />
        </div>

        {/* Ospite */}
        <div className="flex-1 flex flex-col items-end gap-3">
          <TeamBlock
            side="away"
            name={state.awayTeam || '—'}
            logo={state.awayTeamLogo}
            setsWon={state.setsWon.away}
            timeoutsUsed={state.timeoutsUsed.away}
            hasService={!showFinalSets && state.serviceTeam === 'away'}
            setsDisplay={showFinalSets ? 'number' : 'dots'}
            theme={state.scorebugTheme}
          />
        </div>
      </div>
    </div>
  )
}
